import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Play, Square, Plus, Lock } from "lucide-react";
import { usePackAccess } from "@/components/holiday/usePackAccess";
import { useI18n } from "@/context/I18nContext";
import { playSfx } from "@/components/editor/stickers/fx";
import { Chips } from "@/components/editor/stickers/StickerOptions";
import { FavStar } from "@/components/editor/stickers/FavStar";

export const SfxGrid = ({ cat, onAdd, favs, toggleFav }) => {
  const { t, lang } = useI18n();
  const [c, setC] = useState("all");
  const [playing, setPlaying] = useState(null);
  const audio = useRef(null);
  const { visible, locked, catLabel } = usePackAccess(cat.holiday_cats);
  const nav = useNavigate();
  const add = (s) => {
    if (locked(s.pack)) {
      toast(t("hol.locked"), { action: { label: t("hol.unlock"), onClick: () => nav("/credits#holiday-packs") } });
      return;
    }
    onAdd(s.id);
  };
  const toggle = (id) => {
    audio.current?.pause();
    if (playing === id) return setPlaying(null);
    audio.current = playSfx(id);
    audio.current.onended = () => setPlaying(null);
    setPlaying(id);
  };
  const list = cat.sfx.filter((s) => !s.pack || visible.includes(s.pack)).filter((s) => c === "all" || (c === "fav" ? favs.sfx.includes(s.id) : s.cat === c));
  return (
    <div className="space-y-2.5">
      <Chips testid="sfx-cat" items={["fav", "all", ...cat.sfx_cats, ...visible]} value={c} onChange={setC} label={(k) => (k === "fav" ? `★ ${t("fav.label")} (${favs.sfx.length})` : catLabel(k) || t(`sfx.cat_${k}`))} />
      {c === "fav" && !list.length && <p className="text-[11px] text-slate-500 py-3" data-testid="sfx-fav-empty">{t("fav.empty")}</p>}
      <div className="grid grid-cols-2 gap-1.5 max-h-64 overflow-y-auto cx-scroll pr-1" data-testid="sfx-grid">
        {list.map((s) => (
          <div key={s.id} className={`flex items-center rounded-lg border overflow-hidden transition-colors ${playing === s.id ? "border-[#3ad7ff]/70 bg-[#3ad7ff]/10" : "border-white/10 bg-white/[0.02]"}`}>
            <button type="button" data-testid={`sfx-play-${s.id}`} onClick={() => toggle(s.id)} className="px-2.5 py-2 text-slate-400 hover:text-[#3ad7ff]">
              {playing === s.id ? <Square className="w-3.5 h-3.5 fill-current" /> : <Play className="w-3.5 h-3.5 fill-current" />}
            </button>
            <span className="flex-1 min-w-0 truncate text-xs text-slate-200">{lang === "fr" ? s.fr : s.en}</span>
            {locked(s.pack) && <Lock className="w-3 h-3 text-amber-300 mr-1 shrink-0" data-testid={`sfx-lock-${s.id}`} />}
            <FavStar on={favs.sfx.includes(s.id)} onClick={() => toggleFav("sfx", s.id)} testid={`sfx-fav-${s.id}`} />
            {playing === s.id && <span className="flex gap-0.5 items-end h-3 mr-1">{[0, 1, 2].map((i) => <i key={i} className="w-0.5 bg-[#3ad7ff] animate-pulse" style={{ height: `${6 + i * 3}px`, animationDelay: `${i * 0.15}s` }} />)}</span>}
            <button type="button" data-testid={`sfx-${s.id}`} onClick={() => add(s)} className="px-2.5 py-2 border-l border-white/10 text-slate-400 hover:text-white hover:bg-[#2f7bff]/20"><Plus className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};
